/**
 * The 3D scenes' colours, read from the design tokens (src/styles/tokens.css) as the page draws them:
 * the theme (light, dark) and the company's own colour included. Kept as CSS colour strings, so this
 * module needs no three.js.
 */
export interface Palette {
  primary: string; accent: string; success: string; warning: string; danger: string; violet: string; sky: string
  foreground: string; muted: string; border: string; background: string; card: string
  gold: string; silver: string; bronze: string
  dark: boolean
}

/** A token's value (`239 84% 60%`) as a colour three.js and the canvas read: `hsl(239, 84%, 60%)`. */
export function hslOf(raw: string, fallback = 'hsl(239, 84%, 60%)'): string {
  const parts = raw.trim().split(/[\s,/]+/).filter(Boolean)
  if (parts.length < 3) return fallback
  return `hsl(${parts[0]}, ${parts[1]}, ${parts[2]})`
}

export function readPalette(root: Element = document.documentElement): Palette {
  const css = getComputedStyle(root)
  const get = (name: string, fallback?: string) => hslOf(css.getPropertyValue(name), fallback)
  const background = css.getPropertyValue('--background').trim()
  const lightness = Number(background.split(/\s+/)[2]?.replace('%', '') ?? '99')
  return {
    primary: get('--primary'), accent: get('--chart-5', 'hsl(270, 65%, 60%)'), success: get('--success', 'hsl(152, 60%, 42%)'),
    warning: get('--warning', 'hsl(35, 92%, 55%)'), danger: get('--danger', 'hsl(0, 75%, 60%)'), violet: get('--chart-5', 'hsl(270, 65%, 60%)'),
    sky: get('--chart-6', 'hsl(217, 91%, 55%)'), foreground: get('--foreground', 'hsl(222, 47%, 11%)'),
    muted: get('--muted-foreground', 'hsl(215, 16%, 47%)'), border: get('--border', 'hsl(220, 13%, 91%)'),
    background: get('--background', 'hsl(220, 20%, 99%)'), card: get('--card', 'hsl(0, 0%, 100%)'),
    gold: get('--medal-gold', 'hsl(43, 90%, 52%)'), silver: get('--medal-silver', 'hsl(215, 14%, 66%)'),
    bronze: get('--medal-bronze', 'hsl(24, 62%, 52%)'),
    dark: lightness < 40,
  }
}
